import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Info, GitBranch, MessageSquareText, Check, X, Search, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// Lot 631 — multi-églises / annexes (console admin › Églises).
const MUTED = { color: "var(--yalola-text-muted)" };
const BLUE = { background: "#EFF6FF", borderColor: "#60A5FA", color: "#1E3A8A" };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "");

/** Encart bleu « Ce responsable dirige déjà… » + « Valider comme annexe de… ». */
export const LeaderChurchesBox = ({ church: c, canManage, onVerified }) => {
  const list = c.leader_churches || [];
  const parents = list.filter((x) => !x.parent_church_id);
  const [parentId, setParentId] = useState(c.parent_church_id && parents.some((x) => x.id === c.parent_church_id) ? c.parent_church_id : (parents[0]?.id || ""));
  const [busy, setBusy] = useState(false);
  if (!list.length) return null;
  const verifyAnnex = async () => {
    if (!parentId) { toast.error("Choisis l'église mère."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/churches/${c.id}/verify`, { parent_church_id: parentId });
      const p = list.find((x) => x.id === parentId);
      toast.success(`Validée comme annexe de « ${p?.name || "l'église mère"} » — le responsable est notifié`);
      onVerified?.();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-xl px-3 py-2.5 border" style={BLUE} data-testid={`church-leader-${c.id}`} data-count={list.length}>
      <div className="flex items-center gap-1.5 text-xs font-bold"><Info size={14} /> Ce responsable dirige déjà {list.length} église{list.length > 1 ? "s" : ""}</div>
      <ul className="mt-1.5 space-y-1">
        {list.map((d) => (
          <li key={d.id} className="text-[11px]">
            • <b>{d.name}</b> — {d.city}, {d.country}{d.verified ? " (vérifiée)" : " (en attente)"}{d.parent_church_id ? " · annexe" : ""}
          </li>
        ))}
      </ul>
      <p className="text-[11px] mt-1.5" style={{ color: "#1E40AF" }}>Un pasteur peut diriger plusieurs assemblées : ce n'est pas un doublon. Tu peux valider cette église comme <b>annexe</b> d'une de ses églises mères (lien visible sur les deux pages), ou la valider seule.</p>
      {canManage && parents.length > 0 && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="h-8 rounded-md border px-2 text-xs bg-white" style={{ borderColor: "#93C5FD", color: "#1E3A8A" }} data-testid={`church-annex-parent-${c.id}`}>
            {parents.map((p) => <option key={p.id} value={p.id}>Annexe de {p.name} — {p.city}</option>)}
          </select>
          <Button size="sm" className="rounded-full" style={{ background: "#1D4ED8", color: "#fff" }} onClick={verifyAnnex} disabled={busy} data-testid={`church-verify-annex-${c.id}`}>
            <GitBranch size={13} className="mr-1" /> {busy ? "Validation…" : "Valider comme annexe"}
          </Button>
        </div>
      )}
    </div>
  );
};

/** Chip d'état « Question envoyée · en attente / répondue » (lien vers le ticket). */
export const AskStatusChip = ({ church: c }) => {
  const tk = c.review_ticket;
  if (!tk) return null;
  const answered = !!tk.answered;
  return (
    <Link to={`/admin?tab=tickets&ticket=${tk.id}`} className="chip text-[10px] mt-2 inline-flex" style={{ background: answered ? "#DCFCE7" : "#FEF3C7", color: answered ? "#166534" : "#92400E" }} data-testid={`church-ask-status-${c.id}`} data-answered={answered ? 1 : 0}>
      <MessageSquareText size={10} /> {answered ? "Le responsable a répondu" : "Question envoyée"}{c.review_asked_at ? ` · ${fmtDate(c.review_asked_at)}` : ""} · voir le ticket
    </Link>
  );
};

const defaultQuestion = (c) => {
  const first = (c.leader_churches || [])[0];
  const who = c.pastor_name ? c.pastor_name.split(" ").slice(-1)[0] : "";
  return `Bonjour${who ? ` ${c.pastor_title ? c.pastor_title + " " : ""}${c.pastor_name}` : ""},\n\n`
    + `Tu as suggéré l'église « ${c.name} » (${c.city})${first ? ` alors que tu es déjà responsable de « ${first.name} »` : ""}. `
    + `S'agit-il bien d'une assemblée distincte (adresse différente) ? Peux-tu nous préciser son adresse et, si c'est une annexe, de quelle église mère ?\n\n`
    + `Merci pour ta réponse — l'équipe Yalola`;
};

/** Dialogue « Poser une question au responsable » → ticket support ouvert au nom du responsable. */
export const AskResponsibleDialog = ({ church: c, onClose, onSent }) => {
  const [msg, setMsg] = useState(() => (c ? defaultQuestion(c) : ""));
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (msg.trim().length < 5) { toast.error("Écris ta question."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/churches/${c.id}/ask-responsible`, { message: msg.trim() });
      toast.success(data.created ? "Question envoyée — un ticket a été ouvert pour le responsable" : "Question ajoutée au ticket existant");
      onSent?.(data); onClose?.();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!c} onOpenChange={(o) => !busy && !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="church-ask-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquareText size={18} /> Question au responsable</DialogTitle></DialogHeader>
        <p className="text-xs" style={MUTED}>
          Destinataire : <b>{[c?.pastor_title, c?.pastor_name].filter(Boolean).join(" ") || c?.suggested_by_user?.name || "le responsable"}</b>. Un ticket support est ouvert en son nom ; il reçoit une notification + un e-mail et répond depuis <b>Support</b>. Tu suis l'échange dans l'onglet Tickets.
        </p>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={8} className="text-sm" data-testid="church-ask-message" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}><X size={14} className="mr-1" /> Annuler</Button>
          <Button className="pill-primary" onClick={send} disabled={busy} data-testid="church-ask-send"><Check size={14} className="mr-1" /> {busy ? "Envoi…" : "Envoyer la question"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Champ « Église mère » du formulaire admin (recherche + détacher). value = editItem, onChange = setEditItem. */
export const ParentChurchField = ({ value, onChange }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/admin/churches?q=${encodeURIComponent(val.trim())}&status=verified&limit=8`);
      setResults((data.churches || []).filter((c) => c.id !== value.id && !c.parent_church_id));
    } catch { setResults([]); }
  };
  const pick = (c) => { onChange({ ...value, parent_church_id: c.id, parent_church_name: c.name }); setQ(""); setResults([]); };
  const detach = () => onChange({ ...value, parent_church_id: "", parent_church_name: "" });
  return (
    <div className="mt-3" data-testid="admin-church-parent-field">
      <Label className="text-xs flex items-center gap-1"><GitBranch size={12} /> Église mère (cette église est une annexe de…)</Label>
      {value.parent_church_id ? (
        <div className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2 mt-1" style={{ borderColor: "#60A5FA", background: "#EFF6FF" }} data-testid="admin-church-parent-selected">
          <span className="text-sm"><Link2 size={12} className="inline mr-1" /> Annexe de <b>{value.parent_church_name || value.parent_church?.name || "…"}</b></span>
          <button type="button" onClick={detach} className="text-xs underline" style={{ color: "#1E40AF" }} data-testid="admin-church-parent-detach">Détacher</button>
        </div>
      ) : (
        <div className="relative mt-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={MUTED} />
          <Input className="pl-8" value={q} onChange={(e) => search(e.target.value)} placeholder="Rechercher l'église mère (nom, ville, responsable)…" data-testid="admin-church-parent-search" />
          {results.length > 0 && (
            <div className="mt-1 border rounded-xl divide-y max-h-48 overflow-y-auto" style={{ borderColor: "var(--yalola-border)" }} data-testid="admin-church-parent-results">
              {results.map((c) => (
                <button key={c.id} type="button" onClick={() => pick(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-[rgba(0,0,0,0.04)]" data-testid={`admin-church-parent-result-${c.id}`}>
                  <span>{c.name}</span> <span className="opacity-50">· {c.city}{c.pastor_name ? ` · ${c.pastor_name}` : ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <p className="text-[11px] mt-1" style={MUTED}>Facultatif. Un seul niveau : une annexe ne peut pas avoir d'annexes. Le lien s'affiche sur les deux pages.</p>
    </div>
  );
};
